import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './accounting-integration.scss';
import { Form, Button, ButtonColor, ButtonVariant } from '@ampol/design-system';
import xeroLogo from '../../assets/xero_software_logo.svg';
import myobLogo from '../../assets/MYOB_Logo.svg';
import AccountingIntegrationInProgress from './accounting-integration-in-progress';
import {
  getEnrolmentStatus,
  getMyobLoginUri,
  myobDisconnect,
  getXeroLoginUri,
  xeroDisconnect,
} from '../../actions/accountingIntegrationActions';
import Validation from 'constants/validation-messages';
import Label from 'constants/labels';
import { trackCustomEvent } from 'utilities/app-insights';
import { selectAccountingIntegrationPageContext } from '../../selectors/accountingIntegrationSelectors';
import { useAccountingIntegrationModals } from '../../hooks/useAccountingIntegrationModals';
import { useAccountingIntegrationFormState } from '../../hooks/useAccountingIntegrationFormState';
import { useAccountingIntegrationViewState } from '../../hooks/useAccountingIntegrationViewState';
import { useAccountingIntegrationHandlers } from '../../hooks/useAccountingIntegrationHandlers';

interface Props {
  accountId: string;
}

const AccountingIntegration = ({ accountId }: Props) => {
  const dispatch = useDispatch();

  const {
    enrolmentStatus,
    assignedGrandfatherClientNumber,
    readOnly,
    loggedInUser,
    assignedAccountNumber,
    myobLoginUri,
    myobLoginUriLoading,
    myobLoginUriLoaded,
    myobLoginUriError,
    myobDisconnectLoading,
    myobDisconnectLoaded,
    xeroLoginUri,
    xeroLoginUriLoading,
    xeroLoginUriLoaded,
    xeroLoginUriError,
    xeroDisconnectLoading,
    xeroDisconnectLoaded,
  } = useSelector(selectAccountingIntegrationPageContext);

  // Derive connection state from enrolmentStatus (source of truth)
  const connectionState = useMemo(() => {
    const status = enrolmentStatus.enrolmentStatus;
    return {
      xeroConnected: status === 'Xero',
      myobConnected: status === 'MYOB',
    };
  }, [enrolmentStatus.enrolmentStatus]);

  const {
    showAccountingIntegrationHome,
    showAccountingIntegrationInProgress,
    organisationName,
    showInProgressView,
    showHomeView,
  } = useAccountingIntegrationViewState();

  const {
    formValues,
    xeroTermsAndConditionsChecked,
    myobTermsAndConditionsChecked,
    xeroConnectButtonDisabled,
    myobConnectButtonDisabled,
    xeroTermsAndConditionsDisabled,
    myobTermsAndConditionsDisabled,
    handleFormChange,
    connectStatusLoading,
    setConnectStatusLoading,
  } = useAccountingIntegrationFormState(connectionState, myobDisconnectLoaded, xeroDisconnectLoaded);

  const { xeroTermsAndConditionsModal, myobTermsAndConditionsModal } = useAccountingIntegrationModals();

  // Initial data fetch
  useEffect(() => {
    dispatch(getEnrolmentStatus(assignedGrandfatherClientNumber, accountId));
    dispatch(getMyobLoginUri(accountId));
    dispatch(getXeroLoginUri(`${assignedGrandfatherClientNumber};${accountId}`));
  }, [dispatch, assignedGrandfatherClientNumber, accountId]);

  const {
    handleConnectToXeroClick,
    handleDisconnectFromXeroClick,
    handleConnectToMyobClick,
    handleDisconnectFromMyobClick,
    handleInProgressCompleteClick,
  } = useAccountingIntegrationHandlers({
    dispatch,
    xeroConnectButtonDisabled,
    myobConnectButtonDisabled,
    xeroDisconnectLoading,
    myobDisconnectLoading,
    xeroLoginUri,
    myobLoginUri,
    showInProgressView,
    showHomeView,
    setConnectStatusLoading,
    loggedInUser,
    assignedAccountNumber,
    assignedGrandfatherClientNumber,
    accountId,
  });

  return (
    <>
      {showAccountingIntegrationHome && xeroLoginUriLoaded && myobLoginUriLoaded && (
        <Form id={'accounting-integration-form'} onChange={handleFormChange} schema={{}} defaultValues={formValues}>
          <div className='accounting-integration__fields accounting-integration__top-row'>
            <div className='accounting-integration__logo'>
              <img src={xeroLogo} className='accounting-integration__xero-logo' />
            </div>
            <div className='accounting-integration__actions'>
              {!connectionState.xeroConnected && (
                <Button
                  color={ButtonColor.Black}
                  label='Connect to Xero'
                  onClick={readOnly ? undefined : handleConnectToXeroClick}
                  variant={ButtonVariant.Secondary}
                  inactive={xeroConnectButtonDisabled || readOnly}
                />
              )}
              {connectionState.xeroConnected && (
                <Button
                  color={ButtonColor.Red}
                  label='Disconnect from Xero'
                  onClick={readOnly ? undefined : handleDisconnectFromXeroClick}
                  variant={ButtonVariant.Primary}
                  inactive={xeroDisconnectLoading || readOnly}
                />
              )}
              <Form.Checkbox
                className='accounting-integration__checkbox'
                color='blue'
                label={[
                  'I agree to the Xero integration ',
                  <a
                    key='xero-terms-and-conditions'
                    href='#'
                    className='accounting-integration__terms-and-conditions'
                    onClick={() => xeroTermsAndConditionsModal.open()}
                  >
                    terms and conditions
                  </a>,
                ]}
                name='xeroTermsAndConditions'
                disabled={xeroTermsAndConditionsDisabled || readOnly}
              />
            </div>
          </div>
          <div className='accounting-integration__fields'>
            <div className='accounting-integration__logo'>
              <img src={myobLogo} className='accounting-integration__myob-logo' />
            </div>
            <div className='accounting-integration__actions'>
              {!connectionState.myobConnected && (
                <Button
                  color={ButtonColor.Black}
                  label='Connect to MYOB'
                  onClick={readOnly ? undefined : handleConnectToMyobClick}
                  variant={ButtonVariant.Secondary}
                  inactive={myobConnectButtonDisabled || readOnly}
                />
              )}
              {connectionState.myobConnected && (
                <Button
                  color={ButtonColor.Red}
                  label='Disconnect from MYOB'
                  onClick={readOnly ? undefined : handleDisconnectFromMyobClick}
                  variant={ButtonVariant.Primary}
                  inactive={myobDisconnectLoading || readOnly}
                />
              )}
              <Form.Checkbox
                className='accounting-integration__checkbox'
                color='blue'
                label={[
                  'I agree to the MYOB integration ',
                  <a
                    key='myob-terms-and-conditions'
                    href='#'
                    className='accounting-integration__terms-and-conditions'
                    onClick={() => myobTermsAndConditionsModal.open()}
                  >
                    terms and conditions
                  </a>,
                ]}
                name='myobTermsAndConditions'
                disabled={myobTermsAndConditionsDisabled || readOnly}
              />
            </div>
          </div>
        </Form>
      )}

      {showAccountingIntegrationHome &&
        (xeroLoginUriLoading || myobLoginUriLoading) &&
        !(xeroLoginUriError || myobLoginUriError) && <div>{Label.LOADING}</div>}

      {showAccountingIntegrationHome && (xeroLoginUriError || myobLoginUriError) && (
        <div>{Validation.CONTENT_ERROR}</div>
      )}

      {showAccountingIntegrationInProgress && (
        <AccountingIntegrationInProgress
          onDoneClick={handleInProgressCompleteClick}
          organisationName={organisationName}
        />
      )}
    </>
  );
};

export default AccountingIntegration;
